import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorState, EmptyState, Avatar } from "@/components/common";
import { brl, pct, num } from "@/lib/format";
import { toCents, formatBRL } from "@/lib/money";
import Dica from "@/components/Dica";
import { DICAS } from "@/lib/dicas";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AppModal, AppModalPrimaryButton, AppModalSecondaryButton, APP_MODAL_TOKENS } from "@/components/ui/AppModal";
import {
  UsersRound, Pencil, Loader2, Briefcase, AlertTriangle, Search, ChevronDown, ChevronRight,
  Layers, X, Users,
} from "@/components/icons";

const INPUT = "w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40";
const ROLE_ORDER = { diretor_geral: 0, admin: 1, director: 2, manager: 3, seller: 4 };
const ROLE_FILTERS = [
  { k: "all", label: "Todos" },
  { k: "seller", label: "Vendedores" },
  { k: "manager", label: "Gerentes" },
  { k: "director", label: "Diretores" },
  { k: "diretor_geral", label: "Diretor Geral" },
];
const MES_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

// Selo e cores de ritmo — usado na equipe e por vendedor.
function ritmoStatus(ach, ritmo) {
  if (ach == null || ritmo == null || ritmo <= 0) return { k: "indef", label: "Sem ritmo", cls: "bg-slate-100 text-slate-600", tone: "text-muted-foreground", bar: "bg-slate-400" };
  if (ach >= ritmo) return { k: "acima", label: "Acima do ritmo", cls: "bg-emerald-50 text-emerald-700", tone: "text-ok", bar: "bg-ok" };
  if (ach >= ritmo * 0.8) return { k: "perto", label: "Perto do ritmo", cls: "bg-amber-50 text-amber-700", tone: "text-warn", bar: "bg-warn" };
  return { k: "abaixo", label: "Abaixo do ritmo", cls: "bg-rose-50 text-rose-700", tone: "text-bad", bar: "bg-bad" };
}
function fmtDataBR(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? null : d.toLocaleDateString("pt-BR");
}
function plural(n, s, p) { return `${n} ${n === 1 ? s : (p || s + "s")}`; }

export default function Equipe() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, loading, error, reload } = useFetch("/equipe");
  const { data: painel } = useFetch("/sankhya/faturamento/painel?periodo=mes_atual");
  const { data: atencao } = useFetch("/atencao");
  // LOTE R-3a (item 2): contar oportunidades ABERTAS únicas direto de `/deals`
  // (mesmo endpoint do Funil, com RBAC do backend). Antes, `totalOps` somava
  // `m.deals_count` de todos os membros, o que contava em duplicidade (a linha
  // do gerente já inclui a equipe) e ainda somava Fechado/Perdido.
  const { data: dealsAbertos } = useFetch("/deals?apenas_ativas=true");
  const [semIntTotal, setSemIntTotal] = useState(0);
  useEffect(() => {
    if (!["admin", "director", "manager"].includes(user?.role)) return;
    api.get("/equipe/sem-interacao").then((r) => setSemIntTotal(r.data?.total || 0)).catch(() => {});
  }, [user?.role]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const canEdit = ["admin", "director", "manager"].includes(user?.role);
  // Auditoria v4: "Lançar cotas em lote" restrito a Admin + Gerente (Diretor
  // continua editando individualmente suas Contas Diretas pelo modal).
  const canBulk = ["admin", "manager", "director"].includes(user?.role);
  const members = useMemo(() => data || [], [data]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return members.filter((m) => {
      if (roleFilter !== "all" && m.role !== roleFilter) return false;
      if (!query) return true;
      return (m.nome || "").toLowerCase().includes(query)
        || (m.email || "").toLowerCase().includes(query)
        || (m.cargo || "").toLowerCase().includes(query);
    });
  }, [members, q, roleFilter]);

  const totalOps = useMemo(() => {
    // LOTE R-3a (item 2): contagem única de oportunidades abertas do escopo.
    // `/deals?apenas_ativas=true` já exclui Fechado/Perdido e respeita o RBAC
    // (gerente vê equipe, diretor vê diretoria, DG vê tudo). Mantém a mesma
    // base do Funil, sem dupla contagem por hierarquia.
    const arr = Array.isArray(dealsAbertos) ? dealsAbertos : [];
    const ids = new Set();
    for (const d of arr) { if (d?.id) ids.add(d.id); }
    return ids.size;
  }, [dealsAbertos]);

  // Agrupamento hierárquico: DG/Admin → Diretor → Gerente → Vendedores
  const groups = useMemo(() => buildHierarchy(filtered), [filtered]);

  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={reload} />;

  // Totais da equipe (mesma lógica da versão antiga).
  const totalMetaEquipes = members.filter((m) => m.role === "seller").reduce((s, m) => s + (m.goal || 0), 0);
  const totalFatEquipes = members.filter((m) => m.role === "seller").reduce((s, m) => s + (m.invoiced_sales || 0), 0);
  const totalMetaCd = members.filter((m) => m.role === "director" || m.role === "manager").reduce((s, m) => s + (m.goal || 0), 0);
  const totalFatCd = members.filter((m) => m.role === "director" || m.role === "manager").reduce((s, m) => s + (m.invoiced_sales || 0), 0);
  const totalMeta = totalMetaEquipes + totalMetaCd;
  const totalFat = totalFatEquipes + totalFatCd;
  const avgAchExato = totalMeta ? (totalFat / totalMeta * 100) : 0;
  const avgAch = Math.round(avgAchExato * 10) / 10;

  // Ritmo (vem do mesmo cálculo do Faturamento).
  const du = painel?.dias_uteis || {};
  const ritmoPct = du.ritmo_esperado_pct || 0;
  const ritmoDecorridos = du.decorridos || 0;
  const ritmoTotal = du.total || 0;
  const ritmoRestantes = du.restantes != null ? du.restantes : Math.max(ritmoTotal - ritmoDecorridos, 0);
  const ritmoEquipe = ritmoStatus(avgAchExato, ritmoPct);
  const falta = Math.max(0, totalMeta - totalFat);
  const precisaDia = ritmoRestantes > 0 ? falta / ritmoRestantes : 0;
  const metaBatida = totalMeta > 0 && totalFat >= totalMeta;

  // Mês atual (rótulo).
  const now = new Date();
  const mesLabel = `${MES_PT[now.getMonth()]}/${now.getFullYear()}`;
  const mesLabelCurto = `${MES_PT[now.getMonth()]}`;

  // "para bater em dd/mm" — último dia útil do mês.
  const fimMes = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  while (fimMes.getDay() === 0 || fimMes.getDay() === 6) fimMes.setDate(fimMes.getDate() - 1);
  const paraBaterEm = `${String(fimMes.getDate()).padStart(2, "0")}/${String(fimMes.getMonth() + 1).padStart(2, "0")}`;

  const dataSankhya = fmtDataBR(painel?.atualizado_em);
  const subtitle = `${plural(members.length, "membro")}${dataSankhya ? ` · dados Sankhya de ${dataSankhya}` : ""}`;

  return (
    <div>
      <PageHeader
        title="Equipe"
        subtitle={subtitle}
        action={(
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-semibold text-navy" data-testid="equipe-mes-label">{mesLabel}</span>
            {canBulk ? (
              <button
                onClick={() => navigate("/equipe/cotas-lote")}
                data-testid="lancar-cotas-lote-btn"
                className="flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover"
              >
                <Layers className="w-4 h-4" /> Lançar cotas
              </button>
            ) : null}
          </div>
        )}
      />
      {members.length === 0 ? (
        <EmptyState icon={UsersRound} title="Sem equipe vinculada" subtitle="Vendedores aparecem aqui ao vincular você como gerente." />
      ) : (
        <>
          {/* Bloco principal (esq.) + Atenção (dir.) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <ResumoEquipePrincipal
              mesLabel={mesLabel}
              mesCurto={mesLabelCurto}
              totalFat={totalFat}
              totalMeta={totalMeta}
              avgAchExato={avgAchExato}
              avgAch={avgAch}
              ritmoPct={ritmoPct}
              ritmoDecorridos={ritmoDecorridos}
              ritmoTotal={ritmoTotal}
              ritmoRestantes={ritmoRestantes}
              falta={falta}
              precisaDia={precisaDia}
              paraBaterEm={paraBaterEm}
              metaBatida={metaBatida}
              ritmoEquipe={ritmoEquipe}
              totalOps={totalOps}
              totalMetaEquipes={totalMetaEquipes}
              totalMetaCd={totalMetaCd}
            />
            <AtencaoBloco atencao={atencao} semIntTotal={semIntTotal} navigate={navigate} />
          </div>

          {/* Busca + filtro por papel */}
          <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por nome, cargo ou e-mail…"
                className={INPUT + " pl-9"}
                data-testid="equipe-busca"
              />
              {q ? (
                <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy" aria-label="Limpar busca">
                  <X className="w-4 h-4" />
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1" data-testid="equipe-filtro-papel">
              {ROLE_FILTERS.map((rf) => (
                <button
                  key={rf.k}
                  onClick={() => setRoleFilter(rf.k)}
                  className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors ${roleFilter === rf.k ? "bg-white text-navy shadow-sm" : "text-muted-foreground hover:text-navy"}`}
                  data-testid={`filtro-${rf.k}`}
                >
                  {rf.label}
                </button>
              ))}
            </div>
          </div>

          {/* Listagem hierárquica */}
          {filtered.length === 0 ? (
            <EmptyState icon={UsersRound} title="Nenhum membro encontrado" subtitle="Ajuste a busca ou o filtro por papel." />
          ) : (
            <div className="space-y-3">
              {groups.map((g) => (
                <HierarchyGroup
                  key={g.key}
                  group={g}
                  canEdit={canEdit}
                  ritmoPct={ritmoPct}
                  onOpen={(m) => navigate(`/membro/${m.id}`)}
                  onEdit={(m) => setEditing(m)}
                  onFilter={(m, kind) => {
                    const nome = encodeURIComponent(m.nome || "");
                    if (kind === "op") navigate(`/pipeline?owner=${nome}`);
                    else if (kind === "ganhas") navigate(`/pipeline?owner=${nome}&stage=Fechado`);
                    else if (kind === "pend") navigate(`/atividades?tab=equipe&owner=${encodeURIComponent(m.id)}`);
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      {editing ? (
        <MonthlyQuotaDialog member={editing} onClose={() => setEditing(null)} onSaved={reload} />
      ) : null}
    </div>
  );
}

// Barra de progresso com marca vertical do ritmo de hoje.
function BarraRitmo({ pct, ritmoPct, tone, altura = "h-2", testId }) {
  const w = Math.max(0, Math.min(100, pct || 0));
  const rp = Math.max(0, Math.min(100, ritmoPct || 0));
  return (
    <div className={`relative ${altura} bg-slate-100 rounded-full overflow-visible`} data-testid={testId}>
      <div className={`absolute inset-y-0 left-0 ${tone || "bg-brand"} rounded-full transition-all duration-500`} style={{ width: `${w}%` }} />
      {rp > 0 && rp <= 100 ? (
        <div className="absolute top-[-3px] bottom-[-3px] w-[2px] bg-navy/80 rounded-full" style={{ left: `calc(${rp}% - 1px)` }} title={`Ritmo esperado: ${rp}%`} />
      ) : null}
    </div>
  );
}

function ResumoEquipePrincipal({ mesLabel, mesCurto, totalFat, totalMeta, avgAchExato, avgAch, ritmoPct, ritmoDecorridos, ritmoTotal, ritmoRestantes, falta, precisaDia, paraBaterEm, metaBatida, ritmoEquipe, totalOps, totalMetaEquipes, totalMetaCd }) {
  return (
    <div className="lg:col-span-2 rounded-2xl border border-black/5 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="equipe-resumo-principal">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" data-testid="equipe-faturado-label">Faturado da equipe · {mesLabel}</div>
          <div
            className="font-bold text-navy mt-1 tabular-nums whitespace-nowrap"
            style={{ fontSize: "clamp(20px, 4.4vw, 32px)" }}
            data-testid="equipe-faturado-grande"
            title={brl(totalFat)}
          >{brl(totalFat)}</div>
          <div className="text-xs text-muted-foreground mt-0.5">de <span className="font-medium text-navy">{brl(totalMeta)}</span> de meta</div>
        </div>
        <div className="text-right shrink-0">
          <div className={`font-bold ${ritmoEquipe.tone} tabular-nums`} style={{ fontSize: "clamp(20px, 4.4vw, 32px)" }} data-testid="equipe-pct-grande">
            {avgAch}%
          </div>
          <span className={`inline-block mt-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${ritmoEquipe.cls}`} data-testid="equipe-ritmo-selo">
            {ritmoEquipe.label}
          </span>
        </div>
      </div>

      <div className="mt-4" data-testid="equipe-barra-ritmo">
        <BarraRitmo pct={avgAchExato} ritmoPct={ritmoPct} tone={ritmoEquipe.bar} />
        <div className="mt-1.5 text-[11px] text-muted-foreground text-right">
          ritmo de hoje <span className="font-medium text-navy">{ritmoPct}%</span>
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-navy">
          <span data-testid="equipe-dias">{ritmoDecorridos} de {ritmoTotal} dias úteis</span>
          <span data-testid="equipe-faltam">
            {metaBatida ? <span className="text-ok font-semibold">Meta batida</span> : <>faltam <span className="font-semibold tabular-nums">{brl(falta)}</span></>}
          </span>
        </div>
      </div>

      {/* 3 indicadores em linha */}
      <div className="mt-4 pt-4 border-t border-black/5 grid grid-cols-1 sm:grid-cols-3 gap-4 divide-y sm:divide-y-0 sm:divide-x divide-black/5">
        <div className="sm:pr-4 pt-4 sm:pt-0" data-testid="equipe-ind-por-dia">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Precisa por dia útil</div>
          {metaBatida ? (
            <div className="text-ok font-semibold text-base mt-1">Meta batida</div>
          ) : (
            <>
              <div className="text-navy font-semibold tabular-nums mt-1" style={{ fontSize: "clamp(14px, 3.3vw, 18px)" }}>{brl(precisaDia)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">para bater em {paraBaterEm}</div>
            </>
          )}
        </div>
        <div className="sm:px-4 pt-4 sm:pt-0" data-testid="equipe-ind-ops">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Oportunidades abertas</div>
          <div className="text-navy font-semibold tabular-nums mt-1" style={{ fontSize: "clamp(14px, 3.3vw, 18px)" }}>{num(totalOps)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">em {mesCurto}</div>
        </div>
        <div className="sm:pl-4 pt-4 sm:pt-0" data-testid="equipe-ind-composicao">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Composição da meta</div>
          {(totalMetaEquipes || totalMetaCd) ? (
            <>
              <div className="text-navy font-semibold mt-1 text-sm tabular-nums">
                Equipes {brl(totalMetaEquipes)}
              </div>
              {totalMetaCd > 0 ? (
                <div className="text-navy text-sm tabular-nums">
                  Contas diretas {brl(totalMetaCd)}
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-muted-foreground italic text-sm mt-1">Sem meta definida</div>
          )}
        </div>
      </div>
    </div>
  );
}

function AtencaoBloco({ atencao, semIntTotal, navigate }) {
  const baseItems = atencao?.items || [];
  // Injeta "clientes sem interação" (o antigo botão) para gestores.
  const items = useMemo(() => {
    const arr = [...baseItems];
    if (semIntTotal && semIntTotal > 0) {
      arr.push({
        foco: "clientes_sem_interacao",
        titulo: "Clientes sem interação",
        severidade: "media",
        count: semIntTotal,
      });
    }
    return arr;
  }, [baseItems, semIntTotal]);
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="equipe-atencao">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-navy" />
        <div className="text-sm font-semibold text-navy">O que pede sua atenção</div>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground py-6 text-center">Nenhuma pendência agora.</div>
      ) : (
        <ul className="divide-y divide-black/5" data-testid="atencao-list">
          {items.map((it, i) => {
            const dot = it.severidade === "alta" ? "bg-bad" : it.severidade === "media" ? "bg-warn" : "bg-info";
            const target = it.foco === "clientes_sem_interacao" ? "/carteira?visao=sem-interacao" : `/foco/${it.foco}`;
            return (
              <li key={i}>
                <button
                  onClick={() => navigate(target)}
                  className="w-full text-left py-2.5 flex items-center gap-3 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors"
                  data-testid={`atencao-row-${it.foco}`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} aria-hidden />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-navy truncate">{it.titulo}</div>
                  </div>
                  <div className="text-sm font-semibold text-navy tabular-nums shrink-0">{num(it.count)}</div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Constrói árvore Diretor Geral → Diretor → Gerente → Vendedor a partir da
// lista plana de /equipe. IDs de gestor vêm de member.gestor_id/hierarquia
// caso backend forneça; caso contrário caímos em heurística por cargo.
function buildHierarchy(members) {
  // Ordenar por peso do papel, depois nome
  const sorted = [...members].sort((a, b) =>
    (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || (a.nome || "").localeCompare(b.nome || "", "pt-BR")
  );
  const groups = [];
  // Diretor Geral / Admin — cabeçalhos institucionais no topo
  const top = sorted.filter((m) => m.role === "diretor_geral" || m.role === "admin");
  if (top.length) groups.push({ key: "top", title: "Diretoria Geral · Administração", members: top, roles: ["diretor_geral", "admin"] });
  // Cada diretor com seus gerentes e vendedores (por manager_id/director_id se disponível)
  const diretores = sorted.filter((m) => m.role === "director");
  const gerentes = sorted.filter((m) => m.role === "manager");
  const vendedores = sorted.filter((m) => m.role === "seller");
  // Sub-diretor: se `director_id` aponta para outro diretor visível, ele é
  // "filho" desse diretor pai — não aparece como grupo top-level.
  const idDiretor = new Set(diretores.map((d) => d.id));
  const subDirsDe = {};
  const diretoresTop = [];
  for (const d of diretores) {
    if (d.director_id && idDiretor.has(d.director_id)) {
      (subDirsDe[d.director_id] = subDirsDe[d.director_id] || []).push(d);
    } else {
      diretoresTop.push(d);
    }
  }
  const restanteGerentes = new Set(gerentes.map((g) => g.id));
  const restanteVend = new Set(vendedores.map((v) => v.id));
  for (const d of diretoresTop) {
    const sub = [];
    // Sub-diretores dentro da diretoria pai (aninhados como um bloco visual).
    for (const sd of subDirsDe[d.id] || []) {
      const sdMgrs = gerentes.filter((g) => g.director_id === sd.id);
      const sdBlock = { ...sd, virtual: true, virtualLabel: `Sub-diretoria: ${sd.nome}`, children: [] };
      for (const g of sdMgrs) {
        restanteGerentes.delete(g.id);
        const vs = vendedores.filter((v) => v.manager_id === g.id);
        vs.forEach((v) => restanteVend.delete(v.id));
        sdBlock.children.push({ ...g, virtual: true, virtualLabel: g.nome, children: vs });
      }
      const sdDiretos = vendedores.filter((v) => restanteVend.has(v.id) && v.director_id === sd.id);
      sdDiretos.forEach((v) => restanteVend.delete(v.id));
      if (sdDiretos.length) sdBlock.children.push({ virtual: true, virtualLabel: "Vendedores diretos", children: sdDiretos });
      sub.push(sdBlock);
    }
    const mgrs = gerentes.filter((g) => g.director_id === d.id || g.gestor_id === d.id);
    for (const g of mgrs) {
      restanteGerentes.delete(g.id);
      const vs = vendedores.filter((v) => v.manager_id === g.id || v.gestor_id === g.id);
      vs.forEach((v) => restanteVend.delete(v.id));
      sub.push({ ...g, children: vs });
    }
    // "Vendedores diretos do Diretor" — só aqueles que NÃO passaram por um
    // gerente vinculado ao diretor. Vendedor com manager_id + director_id
    // não entra aqui (já foi contabilizado via gerente).
    const diretos = vendedores.filter((v) => restanteVend.has(v.id)
      && (v.director_id === d.id || v.manager_id === d.id));
    diretos.forEach((v) => restanteVend.delete(v.id));
    if (diretos.length) sub.push({ ...d, virtual: true, virtualLabel: "Vendedores diretos do Diretor", children: diretos });
    groups.push({ key: `d-${d.id}`, title: `${d.nome} — Diretoria`, director: d, members: [d], children: sub, roles: ["director"] });
  }
  // Gerentes sem diretor (órfãos)
  const gerentesOrfaos = gerentes.filter((g) => restanteGerentes.has(g.id));
  for (const g of gerentesOrfaos) {
    const vs = vendedores.filter((v) => v.manager_id === g.id || v.gestor_id === g.id);
    vs.forEach((v) => restanteVend.delete(v.id));
    groups.push({ key: `g-${g.id}`, title: `${g.nome} — Gerência`, members: [g], children: [{ ...g, virtual: true, virtualLabel: "Equipe", children: vs }], roles: ["manager"] });
  }
  // Vendedores sem hierarquia (independentes)
  const independentes = vendedores.filter((v) => restanteVend.has(v.id));
  if (independentes.length) groups.push({ key: "indep", title: "Vendedores independentes", members: independentes, roles: ["seller"] });
  // Se o membro logado é um seller (vê só seu gerente), tudo cai em `top`. OK.
  return groups.filter((g) => (g.members?.length || 0) + (g.children?.reduce((s, c) => s + (c.children?.length || 0) + 1, 0) || 0) > 0);
}

function HierarchyGroup({ group, canEdit, ritmoPct, onOpen, onEdit, onFilter }) {
  const [open, setOpen] = useState(true);
  const total = (group.members?.length || 0) + (group.children?.reduce((s, c) => s + (c.children?.length || 0) + (c.virtual ? 0 : 1), 0) || 0);
  // Ordena por % atingido do maior para o menor dentro de cada grupo.
  const sortByAch = (arr) => [...(arr || [])].sort((a, b) => {
    const aa = a.achievement_rollup ?? a.achievement ?? 0;
    const bb = b.achievement_rollup ?? b.achievement ?? 0;
    return bb - aa;
  });
  const sortedMembers = sortByAch(group.members);
  const sortedChildren = (group.children || []).map((sub) => sub.virtual ? { ...sub, children: sortByAch(sub.children) } : sub);
  return (
    <section className="rounded-2xl border border-black/5 bg-white overflow-hidden" data-testid={`grupo-${group.key}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50 text-left"
        data-testid={`grupo-toggle-${group.key}`}
      >
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <div className="flex-1">
          <div className="text-sm font-semibold text-navy">{group.title}</div>
          <div className="text-xs text-muted-foreground">{plural(total, "membro")}</div>
        </div>
      </button>
      {open ? (
        <div className="border-t border-black/5">
          {sortedMembers.map((m) => (
            <MemberRow key={`m-${m.id}`} m={m} canEdit={canEdit} ritmoPct={ritmoPct} onOpen={() => onOpen(m)} onEdit={() => onEdit(m)} onFilter={(k) => onFilter(m, k)} />
          ))}
          {sortedChildren.map((sub) => (
            <div key={sub.virtual ? `v-${sub.id}` : `c-${sub.id}`} className="border-t border-black/5">
              {!sub.virtual ? (
                <MemberRow m={sub} canEdit={canEdit} ritmoPct={ritmoPct} onOpen={() => onOpen(sub)} onEdit={() => onEdit(sub)} onFilter={(k) => onFilter(sub, k)} nested />
              ) : (
                <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-muted-foreground bg-slate-50">
                  {sub.virtualLabel}
                </div>
              )}
              {sub.children?.map((v) => (
                <MemberRow key={`v-${v.id}`} m={v} canEdit={canEdit} ritmoPct={ritmoPct} onOpen={() => onOpen(v)} onEdit={() => onEdit(v)} onFilter={(k) => onFilter(v, k)} nested="deep" />
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function MemberRow({ m, canEdit, ritmoPct, onOpen, onEdit, onFilter, nested }) {
  const [expanded, setExpanded] = useState(false);
  const isDirector = m.role === "director";
  const isSeller = m.role === "seller";
  const isTop = m.role === "diretor_geral" || m.role === "admin";
  const metaVal = m.goal_rollup ?? m.goal;
  const fatVal = m.invoiced_sales_rollup ?? m.invoiced_sales;
  const ach = m.achievement_rollup ?? m.achievement ?? 0;
  const showQuota = !isTop && metaVal != null;
  const indent = nested === "deep" ? "pl-12" : nested ? "pl-8" : "pl-4";
  const rs = ritmoStatus(ach, ritmoPct);
  return (
    <div className={`border-t border-black/5 first:border-t-0`} data-testid={`membro-row-${m.id}`}>
      {/* Desktop/tablet */}
      <div className={`hidden sm:flex items-center gap-3 py-3 pr-4 ${indent} hover:bg-slate-50 transition-colors`}>
        <button onClick={() => setExpanded((e) => !e)} className="text-muted-foreground shrink-0" data-testid={`expandir-${m.id}`} aria-label={expanded ? "Recolher" : "Expandir"}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <Avatar name={m.nome} src={m.avatar} size={36} />
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
          <div className="text-sm font-semibold text-navy truncate">{m.nome}</div>
          <div className="text-[11px] text-muted-foreground truncate">{m.cargo} · {m.email}</div>
        </div>
        {showQuota ? (
          <div className="hidden md:block w-[160px] shrink-0" data-testid={`membro-barra-${m.id}`}>
            <BarraRitmo pct={ach} ritmoPct={ritmoPct} tone={rs.bar} altura="h-1.5" />
          </div>
        ) : <div className="hidden md:block w-[160px] shrink-0" />}
        {showQuota ? (
          <div className="hidden md:flex flex-col items-end min-w-[180px]">
            <div className="text-[11px] text-muted-foreground">Meta / Faturado</div>
            <div className="text-sm text-navy">{brl(metaVal)} / <span className="text-muted-foreground">{brl(fatVal)}</span></div>
          </div>
        ) : (
          <div className="hidden md:block text-[11px] text-muted-foreground italic min-w-[180px] text-right">
            {isTop ? "Sem meta individual" : ""}
          </div>
        )}
        {showQuota ? (
          <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold tabular-nums ${rs.cls}`} data-testid={`membro-pct-${m.id}`} title={rs.label}>
            {pct(ach)}
          </span>
        ) : <div className="min-w-[52px]" />}
        {canEdit && (isSeller || isDirector) ? (
          <button onClick={onEdit} data-testid={`edit-quota-${m.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title={isDirector ? "Editar contas diretas" : "Editar cota"}>
            <Pencil className="w-4 h-4" />
          </button>
        ) : null}
      </div>

      {/* Mobile — cartão por vendedor */}
      <div className={`sm:hidden ${indent} pr-4 py-3`} data-testid={`membro-card-${m.id}`}>
        <div className="flex items-start gap-3">
          <Avatar name={m.nome} src={m.avatar} size={36} />
          <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
            <div className="text-sm font-semibold text-navy truncate">{m.nome}</div>
            <div className="text-[11px] text-muted-foreground truncate">{m.cargo}</div>
          </div>
          {showQuota ? (
            <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums shrink-0 ${rs.cls}`}>{pct(ach)}</span>
          ) : null}
          {canEdit && (isSeller || isDirector) ? (
            <button onClick={onEdit} data-testid={`edit-quota-mob-${m.id}`} className="p-2 -mr-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Editar cota">
              <Pencil className="w-4 h-4" />
            </button>
          ) : null}
        </div>
        {showQuota ? (
          <div className="mt-2.5">
            <BarraRitmo pct={ach} ritmoPct={ritmoPct} tone={rs.bar} altura="h-1.5" />
            <div className="mt-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>Meta <span className="font-semibold text-navy tabular-nums">{brl(metaVal)}</span></span>
              <span>Faturado <span className="font-semibold text-navy tabular-nums">{brl(fatVal)}</span></span>
            </div>
          </div>
        ) : (
          <div className="mt-1.5 text-[11px] text-muted-foreground italic">{isTop ? "Sem meta individual" : ""}</div>
        )}
      </div>

      {expanded ? (
        <div className={`pb-4 pr-4 ${indent}`}>
          <div className="grid grid-cols-3 gap-2 max-w-md">
            <StatBtn testId={`membro-${m.id}-op`} count={m.deals_count} label="Op." onClick={() => onFilter("op")} nome={m.nome} />
            <StatBtn testId={`membro-${m.id}-ganhas`} count={m.won_count} label="Ganhas" onClick={() => onFilter("ganhas")} nome={m.nome} />
            <StatBtn testId={`membro-${m.id}-pend`} count={m.atividades_pendentes} label="Pend." onClick={() => onFilter("pend")} nome={m.nome} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function StatBtn({ testId, count, label, onClick, nome }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group text-center rounded-lg px-2 py-1.5 hover:bg-brand-soft/50 focus:outline-none focus:ring-2 focus:ring-brand/40 transition-colors cursor-pointer"
      title={`Ver ${label.toLowerCase()} de ${nome}`}
    >
      <div className="text-sm font-semibold text-brand group-hover:text-brand-hover underline decoration-dotted underline-offset-2">{num(count)}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </button>
  );
}

function MonthlyQuotaDialog({ member, onClose, onSaved }) {
  const { user: viewer } = useAuth();
  const viewerRole = viewer?.role;
  const isDirector = member.role === "director";
  const [history, setHistory] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  // Trabalhamos com centavos internamente (evita erro de arredondamento em números grandes)
  const [goalCents, setGoalCents] = useState(0);
  const [invCents, setInvCents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editGoal, setEditGoal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Janela fixa é aplicada pelo backend — não passamos mais `months`.
      const r = await api.get(`/performance/users/${member.id}/history`);
      const months = r.data?.months || [];
      setHistory(months);
      const curIdx = months.findIndex((mm) => mm.is_current);
      const idx = curIdx >= 0 ? curIdx : 0;
      setSelectedIdx(idx);
      const mm = months[idx];
      if (mm) {
        setGoalCents(Math.round((mm.goal || 0) * 100));
        setInvCents(Math.round((mm.invoiced_sales || 0) * 100));
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  }, [member.id]);

  useEffect(() => { load(); }, [load]);

  const selectMonth = (idx) => {
    if (!history) return;
    setSelectedIdx(idx);
    setEditGoal(false);
    const mm = history[idx];
    setGoalCents(Math.round((mm.goal || 0) * 100));
    setInvCents(Math.round((mm.invoiced_sales || 0) * 100));
  };

  const current = history && selectedIdx >= 0 ? history[selectedIdx] : null;
  const goalReais = goalCents / 100;
  const invReais = invCents / 100;
  const achLive = goalReais > 0 ? Math.round((invReais / goalReais) * 100) : 0;
  const metaZero = goalCents === 0;
  // "Falta para a meta" — cálculo apenas visual, sem dado novo.
  const faltaReais = Math.max(0, goalReais - invReais);
  const superouReais = invReais > goalReais ? invReais - goalReais : 0;

  // Status conforme faixas 85% / 100% (apenas visual).
  const statusInfo = (() => {
    if (metaZero) return { label: "Sem meta", color: APP_MODAL_TOKENS.alertText, bg: APP_MODAL_TOKENS.alertBg };
    if (achLive >= 100) return { label: "Meta atingida", color: APP_MODAL_TOKENS.success, bg: "#E4F4EC" };
    if (achLive >= 85) return { label: "Próximo da meta", color: "#8A6D11", bg: "#FFF6DC" };
    return { label: "Abaixo da meta", color: APP_MODAL_TOKENS.alertText, bg: APP_MODAL_TOKENS.alertBg };
  })();

  // Mini-histórico: os 3 meses ANTES do selecionado (Mês, Meta, Faturado, %)
  const miniHist = useMemo(() => {
    if (!history || selectedIdx < 0) return [];
    return history.slice(Math.max(0, selectedIdx - 3), selectedIdx).reverse();
  }, [history, selectedIdx]);

  // Barras do gráfico: 3 meses passados + mês selecionado.
  const chartMonths = useMemo(() => {
    if (!history || selectedIdx < 0) return [];
    return history.slice(Math.max(0, selectedIdx - 3), selectedIdx + 1);
  }, [history, selectedIdx]);

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      // `invoiced_sales` sai — Sankhya é fonte de verdade agora.
      await api.patch(`/performance/users/${member.id}/month`, {
        year: current.year, month: current.month,
        goal: goalReais,
      });
      toast.success(current.is_current ? "Cota atualizada" : `Cota de ${current.label} atualizada`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  // ---------------- UI ----------------
  const T = APP_MODAL_TOKENS;
  const iniciais = (member.nome || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const MESES_LONG = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  const monthFull = current ? `${MESES_LONG[current.month - 1]}/${current.year}` : "";
  const titleEl = (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div
        aria-hidden
        style={{
          width: 44, height: 44, borderRadius: 999,
          background: "rgba(255,255,255,.14)", color: "#FFFFFF",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontWeight: 700, fontSize: 15, letterSpacing: 0.5,
        }}
        data-testid="quota-avatar"
      >{iniciais}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2 }}>{member.nome}</div>
        <div style={{ color: T.subtitle, fontSize: 12, marginTop: 2 }} data-testid="quota-subtitle">
          {member.cargo}{monthFull ? ` · ${monthFull}` : ""}
        </div>
      </div>
    </div>
  );
  const monthSelect = history && history.length ? (
    <select
      data-testid="quota-month-select"
      value={selectedIdx}
      onChange={(e) => selectMonth(Number(e.target.value))}
      style={{
        height: 32, borderRadius: 8, padding: "0 10px",
        background: "rgba(255,255,255,.1)", color: "#FFFFFF",
        border: "1px solid rgba(255,255,255,.18)", fontSize: 12,
        maxWidth: 180, outline: "none",
      }}
    >
      {history.map((mm, i) => (
        <option key={`${mm.year}-${mm.month}`} value={i} style={{ color: T.text }}>
          {mm.label}{mm.is_current ? " *" : ""}
        </option>
      ))}
    </select>
  ) : null;

  // Anel de progresso (SVG puro).
  const ringPct = Math.min(100, Math.max(0, achLive));
  const ringColor = metaZero ? T.alertText : achLive >= 100 ? T.success : achLive >= 85 ? "#8A6D11" : T.accent;
  const RING = { size: 140, stroke: 12 };
  const r = (RING.size - RING.stroke) / 2;
  const C = 2 * Math.PI * r;
  const dash = (ringPct / 100) * C;

  const footerEl = (
    <>
      <AppModalSecondaryButton onClick={onClose} disabled={saving} data-testid="cancel-quota-btn">
        Cancelar
      </AppModalSecondaryButton>
      <AppModalPrimaryButton onClick={save} disabled={saving || loading || !current} data-testid="save-quota-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salvar
      </AppModalPrimaryButton>
    </>
  );

  // Barras — escala pelo maior entre meta/faturado do conjunto.
  const chartMax = Math.max(
    1,
    ...chartMonths.map((mm) => Math.max(mm.goal || 0, mm.invoiced_sales || 0))
  );

  return (
    <AppModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={titleEl}
      headerAction={monthSelect}
      size="lg"
      testId="quota-modal"
      footer={footerEl}
    >
      {loading ? (
        <div style={{ padding: "32px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: T.textSecondary, fontSize: 14 }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando histórico...
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }} data-testid="quota-body">
          {/* BLOCO 1 — Anel + Valores (duas colunas) */}
          <div className="quota-top-grid" style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 24, alignItems: "center" }}>
            {/* Anel */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
              <svg width={RING.size} height={RING.size} viewBox={`0 0 ${RING.size} ${RING.size}`} data-testid="quota-ring">
                <circle cx={RING.size / 2} cy={RING.size / 2} r={r} fill="none" stroke={T.divider} strokeWidth={RING.stroke} />
                <circle
                  cx={RING.size / 2} cy={RING.size / 2} r={r} fill="none"
                  stroke={ringColor} strokeWidth={RING.stroke} strokeLinecap="round"
                  strokeDasharray={`${dash} ${C}`}
                  transform={`rotate(-90 ${RING.size / 2} ${RING.size / 2})`}
                  style={{ transition: "stroke-dasharray .6s ease, stroke .3s ease" }}
                />
                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize="28" fontWeight="700" fill={T.text} data-testid="quota-ach-live">
                  {metaZero ? "—" : `${achLive}%`}
                </text>
              </svg>
              <div style={{ color: T.textSecondary, fontSize: 11, marginTop: -4 }} data-testid="quota-ach-caption">
                da meta atingida
              </div>
              <span
                data-testid="quota-status-badge"
                style={{
                  background: statusInfo.bg, color: statusInfo.color,
                  padding: "4px 12px", borderRadius: 999,
                  fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase",
                }}
              >{statusInfo.label}</span>
            </div>

            {/* Valores */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <div style={{ color: T.textSecondary, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span aria-hidden style={{ width: 10, height: 10, background: T.accent, borderRadius: 2 }} />
                  Faturado · Sankhya
                </div>
                <div style={{ color: T.text, fontSize: 22, fontWeight: 700, marginTop: 2 }} data-testid="quota-invoiced-read">
                  {current?.is_future
                    ? <span style={{ color: T.textSecondary }}>—</span>
                    : current?.sem_vinculo_sankhya
                      ? <span style={{ color: T.textSecondary, fontStyle: "italic", fontSize: 14 }}>Sem vínculo com o Sankhya</span>
                      : brl(invReais)}
                </div>
              </div>

              <div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  <div style={{ color: T.textSecondary, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span aria-hidden style={{ width: 10, height: 10, background: "#C5D3DE", borderRadius: 2 }} />
                    Meta mensal
                  </div>
                  {canEditGoal(member, isDirector, viewerRole) && !editGoal ? (
                    <button
                      type="button"
                      onClick={() => setEditGoal(true)}
                      data-testid="quota-goal-edit"
                      aria-label="Editar meta"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        color: T.primary, fontSize: 13, fontWeight: 600,
                        background: "transparent", border: "1px solid transparent",
                        borderRadius: 8, padding: "6px 10px",
                        minHeight: 44, cursor: "pointer",
                      }}
                    >
                      <Pencil style={{ width: 14, height: 14 }} /> Editar meta
                    </button>
                  ) : null}
                </div>
                {editGoal ? (
                  <input
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    value={formatBRL(goalCents)}
                    onChange={(e) => setGoalCents(toCents(e.target.value))}
                    onBlur={() => setEditGoal(false)}
                    data-testid="quota-goal"
                    style={{
                      marginTop: 2, height: 36, padding: "0 10px",
                      borderRadius: 8, border: "1px solid " + T.primary,
                      fontSize: 20, fontWeight: 700, color: T.text, width: "100%",
                      outline: "none",
                    }}
                  />
                ) : (
                  <div style={{ color: T.text, fontSize: 22, fontWeight: 700, marginTop: 2 }} data-testid="quota-goal-read">{brl(goalReais)}</div>
                )}
              </div>

              {/* Bloco destacado — Falta para a meta */}
              <div
                data-testid="quota-falta-bloco"
                style={{
                  background: T.highlightBg, borderRadius: T.radiusBlock,
                  padding: "12px 14px", display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12,
                }}
              >
                <div style={{ color: T.textSecondary, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>
                  {superouReais > 0 ? "Meta superada em" : "Falta para a meta"}
                </div>
                <div style={{ color: superouReais > 0 ? T.success : T.text, fontSize: 20, fontWeight: 700 }} data-testid="quota-falta-valor">
                  {superouReais > 0 ? brl(superouReais) : brl(faltaReais)}
                </div>
              </div>
            </div>
          </div>

          {/* Avisos (metaZero / mês futuro / passado) */}
          {metaZero ? (
            <div data-testid="quota-warn-meta-zero" style={{ background: T.alertBg, color: T.alertText, borderRadius: T.radiusBlock, padding: "10px 14px", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
              <AlertTriangle style={{ width: 16, height: 16, flexShrink: 0 }} /> Nenhuma meta definida para este vendedor neste mês.
            </div>
          ) : null}
          {current && current.is_future ? (
            <div style={{ color: T.textSecondary, fontSize: 12 }}>Mês futuro — o faturado só entra depois que o Sankhya trouxer valores.</div>
          ) : current && !current.is_current ? (
            <div style={{ color: T.textSecondary, fontSize: 12 }}>Editando mês passado — a correção fica registrada no histórico.</div>
          ) : null}

          {/* BLOCO 2 — Gráfico + Lista */}
          <div className="quota-bottom-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, borderTop: "1px solid " + T.divider, paddingTop: 20 }}>
            {/* Gráfico Meta x Faturado */}
            <div data-testid="quota-chart">
              <div style={{ color: T.textSecondary, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, marginBottom: 10 }}>
                Meta x Faturado
              </div>
              {chartMonths.length ? (
                <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 150, padding: "0 4px" }}>
                  {chartMonths.map((mm) => {
                    const metaH = Math.round(((mm.goal || 0) / chartMax) * 120);
                    const fatH = Math.round(((mm.invoiced_sales || 0) / chartMax) * 120);
                    return (
                      <div key={`${mm.year}-${mm.month}`} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 120 }}>
                          <div title={`Meta ${brl(mm.goal || 0)}`} style={{ width: 14, height: Math.max(2, metaH), background: "#C5D3DE", borderRadius: 4 }} />
                          <div title={`Faturado ${brl(mm.invoiced_sales || 0)}`} style={{ width: 14, height: Math.max(2, fatH), background: T.accent, borderRadius: 4 }} />
                        </div>
                        <div style={{ color: T.textSecondary, fontSize: 11 }}>{mm.label}{mm.is_current ? "*" : ""}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: T.textSecondary, fontSize: 12 }}>Sem histórico suficiente.</div>
              )}
              <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 11, color: T.textSecondary }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, background: "#C5D3DE", borderRadius: 2 }} /> Meta
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, background: T.accent, borderRadius: 2 }} /> Faturado
                </span>
                <span>* mês em andamento</span>
              </div>
            </div>

            {/* Lista Últimos meses */}
            <div data-testid="quota-mini-historico">
              <div style={{ color: T.textSecondary, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600, marginBottom: 10 }}>
                Últimos meses
              </div>
              {miniHist.length ? (
                <ul style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {miniHist.map((mm) => {
                    const pctM = mm.achievement == null ? null : Math.round(mm.achievement);
                    const st =
                      pctM == null ? { l: "—", c: T.textSecondary, b: "transparent" } :
                      pctM >= 100 ? { l: "Meta atingida", c: T.success, b: "#E4F4EC" } :
                      pctM >= 85 ? { l: "Próximo", c: "#8A6D11", b: "#FFF6DC" } :
                      { l: "Abaixo", c: T.alertText, b: T.alertBg };
                    return (
                      <li key={`${mm.year}-${mm.month}`} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>{mm.label}</div>
                          <div style={{ color: T.textSecondary, fontSize: 12, marginTop: 2 }}>Meta {brl(mm.goal || 0)}</div>
                          <div style={{ color: T.textSecondary, fontSize: 12 }}>Faturado {mm.invoiced_sales == null ? "—" : brl(mm.invoiced_sales)}</div>
                        </div>
                        <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                          <span style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>{pctM == null ? "—" : `${pctM}%`}</span>
                          <span style={{ background: st.b, color: st.c, fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 999, letterSpacing: 0.3, textTransform: "uppercase" }}>{st.l}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div style={{ color: T.textSecondary, fontSize: 12 }}>Sem meses anteriores no histórico.</div>
              )}
            </div>
          </div>

          {isDirector ? (
            <div style={{ background: T.highlightBg, borderRadius: T.radiusBlock, padding: 12, color: T.textSecondary, fontSize: 12 }}>
              Esta é a cota própria do Diretor — representa &ldquo;Contas Diretas do Diretor&rdquo; e é somada
              à meta das equipes na visão consolidada.
            </div>
          ) : null}
        </div>
      )}
    </AppModal>
  );
}

// Lote 3 CRM-006: só Administrador e Gerente editam meta do vendedor.
// A assinatura preserva a chamada atual (member, isDirector); pegamos o
// perfil atual via DOM (variável global do App via localStorage) ou, de
// forma mais robusta, importamos useAuth onde o componente for usado.
function canEditGoal(member, isDirector, viewerRole) {
  // LOTE PERM-1.2: diretor também edita cota de vendedor do escopo (o
  // backend valida escopo e retorna 403 fora dele). `isDirector` indica
  // apenas que o membro em edição é um gerente (bloco "contas diretas");
  // o viewer (quem está logado) é identificado por `viewerRole`.
  void member; void isDirector;
  return viewerRole === "admin" || viewerRole === "manager" || viewerRole === "director";
}
